import React, {FC, useEffect, useState} from "react";
import {
    DeploymentDataEntry,
    Job,
    Measurement,
    MeasurementTypeMapper,
    SyncStatus,
    ValidationStates
} from "../../dtos/dtos";
import {
    discardDeploymentDataEntries,
    getDeploymentDataEntries,
    keepDeploymentDataEntries,
    syncDeploymentDataEntries
} from "../../api";
import DatePicker, {DateObject} from "react-multi-date-picker";
import styles from "./DataEntriesManager.module.scss";
import TimePicker from "react-multi-date-picker/plugins/time_picker";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import {classifyDataEntry, DataEntryClassification, hasErrorForType} from "../../utils/DataEntryValidator";
import {MdCheckCircle, MdSync, MdSyncProblem} from "react-icons/md";


interface DataEntriesManagerProps {
    jobId: string | undefined,
    job: Job | undefined
}

const enum confirmationStatus {
    hidden,
    discard,
    keep,
    sync
}

const DataEntriesManager: FC<DataEntriesManagerProps> = (props) => {
    const [errorMsg, setErrorMsg] = useState<string>();
    const [loadingDataEntries, setLoadingDataEntries] = useState<boolean>(true);
    const [dataEntries, setDataEntries] = useState<DeploymentDataEntry[]>([]);
    const [dataEntryClassification, setDataEntryClassification] = useState<DataEntryClassification[][]>([]);
    const [filterFromTime, setFilterFromTime] = useState(new Date());
    const [filterToTime, setFilterToTime] = useState(new Date());
    const [selected, setSelected] = useState<string[]>([]);
    const [showDiscardConfirmation, setShowConfirmation] = useState<confirmationStatus>(confirmationStatus.hidden);

    const updateDataEntries = () => {
        if (props.jobId) {
            setLoadingDataEntries(true);
            getDeploymentDataEntries(props.jobId, filterFromTime, filterToTime).then(payload => {
                setDataEntries(payload.data)
                setDataEntryClassification(payload.data.map(d => classifyDataEntry(d, props.job)));
                setLoadingDataEntries(false);
                setSelected([]);
            });
        } else {
            setDataEntries([]);
        }
    }

    useEffect(() => {
        var now = new Date();
        now.setMonth(now.getMonth() - 1)
        setFilterFromTime(now);
        setFilterToTime(new Date());

        updateDataEntries();
    },[props.jobId, props.job])

    const confirmationStatusToText = (status: confirmationStatus) =>
        showDiscardConfirmation === confirmationStatus.discard ? "Discard" : showDiscardConfirmation === confirmationStatus.sync ? "Sync" : "Keep";

    const applyFilter = () => {
        updateDataEntries();
    }

    const filterFromTimeChanged = (x: DateObject | DateObject[] | null) => {
        if (x instanceof DateObject) {
            setFilterFromTime(x.toDate());
        } else {
            setErrorMsg("Fejl i ny dato... afbryder at gemme");
            setFilterFromTime(new Date())
        }
    }

    const filterToTimeChanged = (x: DateObject | DateObject[] | null) => {
        if (x instanceof DateObject) {
            setFilterToTime(x.toDate());
        } else {
            setErrorMsg("Fejl i ny dato... afbryder at gemme");
            setFilterToTime(new Date())
        }
    }

    const handleCheckboxChange = (id: string) =>
        setSelected(prev => prev.includes(id) ?
            prev.filter(x => x !== id) :
            [...prev, id]);

    const handleSelectAllClicked = (event: React.ChangeEvent<HTMLInputElement>) =>
        setSelected(prev => event.target.checked ?
            dataEntries.map(x => x.id) :
            []);

    const selectAllWithWarnings = () => {
        setSelected(dataEntries
            .filter((x, i) => !dataEntryClassification[i].includes(DataEntryClassification.valid))
            .map(x => x.id));
    }


    return <div className={styles.dataEntriesManager}>
        { props.jobId &&
            <div className={styles.topOptions}>
                <div className={styles.filterArea}>
                    <h5>Filter data</h5>
                    <table className={styles.dataTable}>
                        <tbody>
                        <tr>
                            <th>From</th>
                            <td><DatePicker
                                multiple={false}
                                value={filterFromTime}
                                onChange={filterFromTimeChanged}
                                weekStartDayIndex={1}
                                plugins={[
                                    <TimePicker position="right"/>
                                ]}
                                // plugins={[<AnalogTimePicker />]}
                                format="D MMM YYYY HH:mm:ss"
                            ></DatePicker></td>
                        </tr>
                        <tr>
                            <th>To</th>
                            <td><DatePicker
                                multiple={false}
                                value={filterToTime}
                                onChange={filterToTimeChanged}
                                weekStartDayIndex={1}
                                plugins={[
                                    <TimePicker position="right"/>
                                ]}
                                // plugins={[<AnalogTimePicker />]}
                                format="D MMM YYYY HH:mm:ss"
                            ></DatePicker></td>
                        </tr>
                        </tbody>
                    </table>
                    <div className={styles.applyFilterArea}>
                        <Button variant="success" onClick={applyFilter}>Apply Filter</Button>
                    </div>
                </div>
                <div className={styles.otherOptions}>
                    <div className={styles.buttonsArea}>
                        <Button onClick={selectAllWithWarnings}>Mark all auto-classified</Button>
                    </div>
                </div>
            </div>
        }
        {!loadingDataEntries && dataEntries && selected.length > 0 &&
            <div className={styles.selectedActionsArea}>
                <h5 className={styles.selectedCountArea}>{selected.length} / {dataEntries?.length} selected</h5>
                <div className={styles.buttonsArea}>
                    <Button variant="warning" onClick={() => setShowConfirmation(confirmationStatus.discard)}>Mark as discarded</Button>
                    <Button variant="success" onClick={() => setShowConfirmation(confirmationStatus.keep)}>Mark as valid</Button>
                    <Button variant="primary" onClick={() => setShowConfirmation(confirmationStatus.sync)}>Re-sync</Button>
                </div>
            </div>
        }
        { loadingDataEntries && <h3>Loading...</h3>}
        { !loadingDataEntries && dataEntries && dataEntryClassification.length === dataEntries.length &&
            <div className={styles.dataContainer}>
                <table>
                    <tbody>
                    <tr>
                        <th>
                            <input
                                type="checkbox"
                                checked={selected.length === dataEntries.length}
                                onChange={handleSelectAllClicked}
                            />
                        </th>
                        <th>Time</th>
                        <th>Data</th>
                        <th>Status</th>
                    </tr>
                    {dataEntries.map((x: DeploymentDataEntry, i: number) =>
                        <tr key={i} onClick={() => handleCheckboxChange(x.id)} className={x.validationStatus === ValidationStates.Discard ? styles.discarded : ''}>
                            <td>
                                <input
                                    type="checkbox"
                                    value={x.id}
                                    checked={selected.includes(x.id)}
                                    readOnly
                                />
                            </td>
                            <td className={styles.date}>{new Date(x.time).toLocaleString()}</td>
                            <td className={styles.data}>
                                {x.gpsCoordinates &&
                                    <div
                                         className={dataEntryClassification[i]?.includes(DataEntryClassification.geoFencingExceeded) ? styles.warning : ''}>
                                        GPS: ({x.gpsCoordinates.latitude.toFixed(6)}, {x.gpsCoordinates.longitude.toFixed(6)}
                                    </div>
                                }

                                {x.measurements.map((m: Measurement, j: number) =>
                                    <div key={j}
                                         className={hasErrorForType(m.type, dataEntryClassification[i]) ? styles.warning : ''}>
                                        {MeasurementTypeMapper[m.type]}: {m.value.toFixed(2)}
                                    </div>
                                )}
                            </td>
                            <td className={styles.status}>
                                {x.syncStatus === SyncStatus.Error &&
                                    <MdSyncProblem className={styles.syncError} />
                                }
                                {x.syncStatus === SyncStatus.Synced &&
                                    <MdCheckCircle className={styles.synced} />
                                }
                                {/*{x.syncStatus === SyncStatus.Synced && x.validationStatus !== ValidationStates.Discard &&*/}
                                {/*    <MdCheckCircle className={styles.validated} />*/}
                                {/*}*/}
                                {x.syncStatus === SyncStatus.Pending &&
                                    <MdSync className={styles.pending} />
                                }
                            </td>
                        </tr>
                    )}
                    </tbody>
                </table>
            </div>
        }
        <Modal show={showDiscardConfirmation !== confirmationStatus.hidden} onHide={() => setShowConfirmation(confirmationStatus.hidden)} backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>Mark {selected.length} measurements as {confirmationStatusToText(showDiscardConfirmation)}?</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <span>Do you really want to mark {selected.length} measurements as {confirmationStatusToText(showDiscardConfirmation)}.</span>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirmation(confirmationStatus.hidden)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={() => {
                        if (showDiscardConfirmation === confirmationStatus.discard) {
                            discardDeploymentDataEntries(props.jobId ?? "", selected).then(
                                x => updateDataEntries(),
                                errorMsg => setErrorMsg(errorMsg?.message || "Unkown error occurred on {confirmationStatusToText(showDiscardConfirmation)}"));
                        } else if (showDiscardConfirmation === confirmationStatus.keep) {
                            keepDeploymentDataEntries(props.jobId ?? "", selected).then(
                                x => updateDataEntries(),
                                errorMsg => setErrorMsg(errorMsg?.message || "Unkown error occurred on {confirmationStatusToText(showDiscardConfirmation)}"));
                        } else if (showDiscardConfirmation === confirmationStatus.sync) {
                            syncDeploymentDataEntries(props.jobId ?? "", selected).then(
                                x => updateDataEntries(),
                                errorMsg => setErrorMsg(errorMsg?.message || "Unkown error occurred on {confirmationStatusToText(showDiscardConfirmation)}"));
}
                        setShowConfirmation(confirmationStatus.hidden);}
                    }>{showDiscardConfirmation === confirmationStatus.discard ? "Discard" : confirmationStatusToText(showDiscardConfirmation)}</Button>
                </Modal.Footer>
            </Modal>
    </div>
}
export default DataEntriesManager;
